/**
 * Onboarding state hook — single source of truth for the welcome modal,
 * dashboard checklist, Industry Architect inline tip, and signature nudge.
 *
 * Backend computes everything in /api/onboarding/state so we never store
 * onboarding state in localStorage (per spec: persists across devices).
 *
 * Existing users are back-filled with onboarding_complete=true so the
 * hook returns shouldShow* flags that are all false for them — none of the
 * onboarding UI ever renders.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';
import { useApi } from '../hooks/useApi';

const OnboardingContext = createContext(null);

export function OnboardingProvider({ children }) {
  const { token } = useAuth();
  const { get, post } = useApi();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!token) {
      setState(null);
      return;
    }
    setLoading(true);
    try {
      const data = await get('/onboarding/state');
      setState(data);
    } catch (e) {
      // Onboarding is non-critical — never crash the rest of the app
      // because of it. Treat fetch failure as "everything dismissed".
      console.warn('onboarding state fetch failed; suppressing onboarding UI', e);
      setState(null);
    } finally {
      setLoading(false);
    }
  }, [token, get]);

  useEffect(() => { refresh(); }, [refresh]);

  // Helpers that POST a dismissal and re-fetch so all subscribers update.
  const dismissWelcome = useCallback(async () => {
    try { await post('/onboarding/skip-welcome', {}); }
    finally { await refresh(); }
  }, [post, refresh]);

  const startArchitect = useCallback(async () => {
    try { await post('/onboarding/start-architect', {}); }
    finally { await refresh(); }
  }, [post, refresh]);

  const dismissChecklist = useCallback(async () => {
    try { await post('/onboarding/dismiss-checklist', {}); }
    finally { await refresh(); }
  }, [post, refresh]);

  const dismissArchitectTip = useCallback(async () => {
    try { await post('/onboarding/dismiss-architect-tip', {}); }
    finally { await refresh(); }
  }, [post, refresh]);

  const markSignatureNudgeShown = useCallback(async () => {
    try { await post('/onboarding/mark-signature-nudge-shown', {}); }
    finally { await refresh(); }
  }, [post, refresh]);

  const value = useMemo(() => ({
    state, loading, refresh,
    dismissWelcome, startArchitect, dismissChecklist,
    dismissArchitectTip, markSignatureNudgeShown,
  }), [state, loading, refresh, dismissWelcome, startArchitect, dismissChecklist, dismissArchitectTip, markSignatureNudgeShown]);

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
}

export function useOnboarding() {
  const ctx = useContext(OnboardingContext);
  // Never throw — non-onboarded callers should just see safe defaults.
  return ctx || {
    state: null, loading: false,
    refresh: async () => {},
    dismissWelcome: async () => {},
    startArchitect: async () => {},
    dismissChecklist: async () => {},
    dismissArchitectTip: async () => {},
    markSignatureNudgeShown: async () => {},
  };
}
